import { Link } from "react-router-dom";
import { routeBuilder } from "../../../utils/routeBuilder";
import { dispatchInfoStrings, debitNoteStrings } from "../../../components/StringBundle";
import { Check, Edit, Delete } from "@material-ui/icons";
import { Tooltip } from "@material-ui/core";
import FieldRenderer from "../../../components/FieldRenderer";
import Template from "../../../components/Template";
import { formatDateSafe, formatNumber, viewDataStructure, mapObjectToArray, normalizeVehicle, dateFormat, isBeforeToday } from "../../common";
import { roles } from "../../../constants/roles";

import { companyOptions } from "../../../utils/constant";
import VehicleInformationTab from "./VehicleInformationTab";
import type { ReactNode } from "react";
import type {
  DispatchGridColumn,
  DispatchGridRow,
  TableColumn,
  TemplateFieldPayload,
  DynamicFormSection,
  CreateSectionDetail,
  VendorCreateFieldsInput,
  LabelValueOption,
  DispatchDetailsData,
  VehicleRowsContext,
  VehicleTableRow,
  CreateDispatchFormValues,
  DispatchViewTab,
  DispatchListRow,
  PoDetailsFormatted,
  DispatchInfoFormatted,
  CreatePoDetailsFormatted,
  PurchaseDetailsState,
} from "./types";
import type { MapObjectToArrayColumnEntry } from "../types";

const dispatchGridColumns = dispatchInfoStrings.grid;
const dispatchFormsStrings = dispatchInfoStrings.forms;

export const GetDispatchInfoGridColumns = (): DispatchGridColumn[] => {
  const result: DispatchGridColumn[] = [];
  for (const id in dispatchGridColumns) {
    const label: string = dispatchGridColumns[id];
    let colDef: DispatchGridColumn | null = null;

    if (id === "dispatch_id") {
      colDef = {
        title: label,
        dataIndex: id,
        key: id,
        render: (text: string | undefined, record: DispatchGridRow) => {
          const vgId = record?.vg_composition_id;
          const dispatchId = record?.dispatch_id;
          return (
            <Link
              to={`/dispatch/${encodeURIComponent(String(vgId))}/view?dispatchId=${encodeURIComponent(String(dispatchId))}`}
            >
              {text}
            </Link>
          );
        },
      };
    } else {
      colDef = {
        title: label,
        dataIndex: id,
        key: id,
      };
    }
    if (colDef) result.push(colDef);
  }
  return result;
};

export const gcTableColumns: TableColumn[] = [
  { key: "composition_name", label: dispatchFormsStrings.item, sx: { width: "70%" } },
  { key: "composition_rate", label: dispatchFormsStrings.composition, sx: { width: "30%" } },
];

// DynamicForm sectionDetails for the "select vendor / PO" step shown when
export const getVendorCreateFields = ({
  suppliersApi,
  itemListApi,
  poListApi,
  entityList = [],
  selectedCompany = null,
  selectedEntity = null,
  selectedSupplier = null,
  selectedItem = null,
}: VendorCreateFieldsInput = {}): DynamicFormSection[] => {
  const entityOptions: LabelValueOption[] = [];
  for (const item of entityList as LabelValueOption[]) {
    if (item.value === "All") continue;
    if (selectedCompany?.label === "CCL" && item.label === "NCCL") continue;
    if (selectedCompany?.label === "NCL" && item.label !== "NCCL") continue;
    entityOptions.push(item);
  }

  return [
    {
      fields: [
        {
          name: "company_name",
          label: dispatchFormsStrings.companyName,
          type: "autocomplete",
          required: true,
          labelKey: "label",
          options: companyOptions || [],
          md: 6,
        },
        {
          name: "entity_name",
          label: dispatchFormsStrings.entity,
          type: "autocomplete",
          required: true,
          labelKey: "label",
          options: entityOptions,
          disabled: !selectedCompany,
          md: 6,
        },
        {
          name: "supplier_name",
          label: dispatchFormsStrings.supplierName,
          type: "asyncAutocomplete",
          required: true,
          labelKey: "vendor_name",
          valueKey: "vendor_id",
          fetchOptions: (search: string) => suppliersApi ? suppliersApi(search) : Promise.resolve([]),
          disabled: !selectedEntity,
          md: 6,
        },
        {
          name: "item_name",
          label: dispatchFormsStrings.itemName,
          type: "asyncAutocomplete",
          required: true,
          labelKey: "item_name",
          valueKey: "item_id",
          fetchOptions: (search: string) => itemListApi ? itemListApi(search) : Promise.resolve([]),
          disabled: !selectedSupplier,
          md: 6,
        },
        {
          name: "po_no",
          label: dispatchFormsStrings.poNo,
          type: "asyncAutocomplete",
          required: true,
          labelKey: "pono",
          valueKey: "pono",
          fetchOptions: (_search: string) => poListApi ? poListApi(_search) : Promise.resolve([]),
          disabled: !selectedItem,
          md: 6,
        },
      ],
    },
  ];
};

const vehicleInfoColumnConfig = dispatchInfoStrings.vehicleInfo;

export const getVehicleInfoColumns = (): TableColumn[] => {
  const result: TableColumn[] = [];
  for (const id in vehicleInfoColumnConfig) {
    result.push({
      key: id,
      label: vehicleInfoColumnConfig[id],
      sx: { whiteSpace: "nowrap" },
    });
  }
  return result;
};

// Builds DynamicView's `otherTabs` prop for view.tsx.
export const getOtherTabs = (dispatchId: string | null): DispatchViewTab[] => [
  {
    label: dispatchInfoStrings.vehicle.vehicleInformationTab,
    index: "vehicles",
    visible: true,
    component: <VehicleInformationTab dispatchId={dispatchId} />,
  },
];

// ── Field configs for view.tsx detail accordions ───────────────────────────────
// Used with viewDataStructure() from screens/common.js (Finance Costing pattern)

type DispatchInfoFieldValue = string | number | undefined;

const poDetailsFormatConfig: Record<string, (v: DispatchInfoFieldValue) => string | number | null> = {
  po_date: (v) => formatDateSafe(v),
  total_quantity: (v) => (v ? formatNumber(v, 3) : "-"),
};

export const getPoDetailsInfo = (data: DispatchDetailsData) => {
  const draft: Record<string, string | number | null> = {};
  for (const key in dispatchInfoStrings.view.purchase_info) {
    draft[key] = poDetailsFormatConfig[key]
      ? poDetailsFormatConfig[key](data?.[key])
      : (data?.[key] ?? null);
  }
  const formattedData = draft as unknown as PoDetailsFormatted;
  if (data?.po_no) {
    formattedData.po_no = (
      <Link to={routeBuilder("purchase-order", data.po_no, "view")}>
        {data.po_no as string}
      </Link>
    );
  }
  return viewDataStructure(
    mapObjectToArray(dispatchInfoStrings.view.purchase_info, formattedData)
  );
};

const dispatchInfoFormatConfig: Record<string, (v: DispatchInfoFieldValue) => string | number | null> = {
  dispatch_date: (v) => formatDateSafe(v),
  invoice_date: (v) => formatDateSafe(v),
  way_bill_date: (v) => formatDateSafe(v),
  quantity: (v) => (v ? formatNumber(v, 3) : "-"),
  dispatch_quantity: (v) => (v ? formatNumber(v, 3) : "-"),
  invoice_quantity: (v) => (v ? formatNumber(v, 3) : "-"),
};

export const getDispatchInfo = (data: DispatchDetailsData) => {
  const draft: Record<string, string | number | null> = {};
  for (const key in dispatchInfoStrings.view.dispatch_info) {
    draft[key] = dispatchInfoFormatConfig[key]
      ? dispatchInfoFormatConfig[key](data?.[key])
      : (data?.[key] ?? null);
  }
  const formattedData = draft as unknown as DispatchInfoFormatted;
  return viewDataStructure(
    mapObjectToArray(dispatchInfoStrings.view.dispatch_info, formattedData)
  );
};

// ── Vehicle columns ───────────────────────────────────────────────────────────
export const vendorVehicleColumns: TableColumn[] = [
  {
    key: "vehicle_no",
    label: dispatchInfoStrings.vehicle.vehicleNumber,
    sx: {verticalAlign: "top"},
  },
  {
    key: "quantity",
    label: dispatchInfoStrings.vehicle.expectedQuantityInVehicle,
    sx: {verticalAlign: "top"},
  },
  {
    key: "bag_weight",
    label: dispatchInfoStrings.vehicle.bagWeight,
    sx: {verticalAlign: "top"},

  },
  {
    key: "actions",
    label: dispatchInfoStrings.vehicle.actions,
    sx: { verticalAlign: "middle", textAlign: "center" },
  },
];

// Builds table rows for Add / Edit / View vehicle modes
export const getVehicleRows = ({ state, control, handlers, strings, classes }: VehicleRowsContext): VehicleTableRow[] => {
  // State values controlling table behavior
  const {
    isAddingVehicle,      // Whether a new vehicle row is being added
    editingVehicleId,    // ID of row currently in edit mode
    vehicleRows,         // Existing vehicle rows
  } = state;

  // Action handlers
  const {
    onBlurVehicleNo,
    onSave,
    onEdit,
    onCancelAdd,
    onDelete,
  } = handlers;

  const vehicleStrings = strings as { vehicle: Record<string, string> };

  /* ---------- Cell render helpers ---------- */

  // Renders vehicle number cell (FieldRenderer input or plain text)
  const renderVehicleNo = (row: { vehicle_no?: string }, isActionMode: boolean) =>
    isActionMode ? (
        <FieldRenderer
          field={{
            type: "text",
            name: "vehicle_no",
            label: "",
            size: "small",
            enableBlurHandler: true,
            onCustomBlur: onBlurVehicleNo,
            transform: normalizeVehicle,
          }}
          control={control}
        />
    ) : (
      row.vehicle_no
    );

  // Renders quantity cell (FieldRenderer input or plain text)
  const renderQuantity = (row: { quantity?: number }, isActionMode: boolean) =>
    isActionMode ? (
      <FieldRenderer
        field={{
          type: "number",
          name: "quantity",
          label: "",
          size: "small",
          numberDigits: "10,2",
        }}
        control={control}
      />
    ) : (
      row.quantity
    );

  // Renders bag weight cell (FieldRenderer input or plain text)
  const renderBagWeight = (row: { bag_weight?: number | string }, isActionMode: boolean) =>
    isActionMode ? (
      <FieldRenderer
        field={{
          type: "number",
          name: "bag_weight",
          label: "",
          size: "small",
          numberDigits: "10,2",
        }}
        control={control}
      />
    ) : (
      row.bag_weight
    );

  // Renders action buttons based on row mode
  const renderActions = (row: { id?: number | string }, mode: string, isEdit: boolean, isActionMode: boolean) =>
    isActionMode ? (
      <div className={classes.actionIconsRowCenter}>
        {/* Save button */}
        <Tooltip
          key={`save-tooltip-${row.id || "add"}`}
          title={vehicleStrings.vehicle.saveVehicleTitle}>
          <Check
            className="clickable success"
            onClick={() => onSave(mode, row)}
          />
        </Tooltip>

        {/* Cancel / Delete while adding or editing */}
        <Tooltip
          key={`cancel-tooltip-${row.id || "add"}`}
          title={vehicleStrings.vehicle.cancelAddVehicleTitle}>
          <Delete
            className={`clickable ${classes.orangeIcon}`}
            onClick={!isEdit ? onCancelAdd : () => onDelete(row.id as string | number)}
          />
        </Tooltip>
      </div>
    ) : (
      <div className={classes.actionIconsRowCenter}>
        {/* Edit existing row */}
        <Tooltip
          key={`edit-tooltip-${row.id}`}
          title={dispatchInfoStrings.vehicle.editVehicleTitle}>
          <Edit
            className={`clickable ${classes.orangeIcon}`}
            onClick={() => onEdit(row as { id: number | string; vehicle_no: string; quantity: number })}
          />
        </Tooltip>

        {/* Delete existing row */}
        <Tooltip
          key={`delete-tooltip-${row.id}`}
          title={vehicleStrings.vehicle.deleteVehicleTitle}>
          <Delete
            className={`clickable ${classes.orangeIcon}`}
            onClick={() => onDelete(row.id as string | number)}
          />
        </Tooltip>
      </div>
    );

  /* ---------- Row builder ---------- */

  // Builds a single table row based on mode
  const buildRow = ({ row = {}, mode }: { row?: { id?: number | string; vehicle_no?: string; quantity?: number; bag_weight?: number | string }; mode: string }): VehicleTableRow => {
    const isEdit = mode === "edit";                 // Editing existing row
    const isActionMode = mode === "add" || isEdit;  // Add or edit mode

    return {
      vehicle_no: renderVehicleNo(row, isActionMode),
      quantity: renderQuantity(row, isActionMode),
      bag_weight: renderBagWeight(row, isActionMode),
      actions: renderActions(row, mode, isEdit, isActionMode),
    };
  };


  // Existing rows (edit mode applied conditionally)
  const existingRows = vehicleRows.map((row) =>
    buildRow({
      row,
      mode: editingVehicleId === row.id ? "edit" : "view",
    })
  );

  // Add row when adding a new vehicle
  const addRow = isAddingVehicle ? [buildRow({ mode: "add" })] : [];
  // Final table rows
  return [...existingRows, ...addRow];
};
const dnViewStrings = debitNoteStrings.view;
export const categoryList: LabelValueOption[] = [
  { label: dnViewStrings.categoryDetails.all, value: "All" },
  { label: dnViewStrings.categoryDetails.import, value: "Import" },
  { label: dnViewStrings.categoryDetails.domestic, value: "Domestic" },
];

// Labels for export_extra_fields come from dispatchInfoStrings.export_extra_fields (StringBundle).
export const DISPATCH_EXPORT_INSERT_POSITIONS: { key: string; position: number }[] = [
  { key: "entity_code", position: 2 },
  { key: "po_category", position: 3 },
  { key: "po_sub_category", position: 4 },
];

export const createDispatchRestrictedRoles: string[] = [
  roles.Auditor,
  roles.executiveLeadership,
  roles.viewOnly,
  roles.readOnly,
  roles.managerfinance,
  roles.Chairman,
  roles.ceo,
  roles.qcManager,
  roles.qcHead,
];

export const createDispatchFormRestrictedRoles: string[] = [
  roles.Auditor,
  roles.executiveLeadership,
  roles.viewOnly,
  roles.readOnly,
  roles.managerfinance,
  roles.Chairman,
  roles.ceo,
  roles.factorymanager,
];


const createpoDetailsFormatConfig: Record<string, (data: PurchaseDetailsState) => string | number | null> = {
  po_date: (data) => formatDateSafe(data?.details?.po_date),
  expected_quantity: (data) =>
    data?.details?.expected_quantity ? formatNumber(data?.details?.expected_quantity) : null,
  supplier_name: (data) => (data?.supplier?.supplier_name as string | undefined) ?? null,
  total_quantity: (data) => data?.details?.total_quantity ? formatNumber(data?.details?.total_quantity, 3) : "-",
};

export const getCreatePoDetails = (data: PurchaseDetailsState) => {
  const draft: Record<string, string | number | null> = {};
  for (const key in dispatchInfoStrings.forms.PO_Details) {
    draft[key] = createpoDetailsFormatConfig[key]
      ? createpoDetailsFormatConfig[key](data)
      : ((data?.details?.[key] as string | number | undefined) ?? null);
  }
  const formattedData = draft as unknown as CreatePoDetailsFormatted;
  return viewDataStructure(
    mapObjectToArray(dispatchInfoStrings.forms.PO_Details, formattedData)
  );
};

const dispatchInfoForms = dispatchInfoStrings.forms;

export const formatGCCompositions = (composition = {}) =>
  mapObjectToArray(
    dispatchInfoForms.composition_info_grid_labels,
    composition,
    "composition_name",
    "composition_rate"
  );

export const getCompositionSchemaFields = (): { name: string; type: string }[] => {
  const result: { name: string; type: string }[] = [];
  for (const name in dispatchInfoForms.composition_info_grid_labels) {
    result.push({ name, type: "number" });
  }
  return result;
};

export const getCompositionColumns = (): TableColumn[] =>
  mapObjectToArray(dispatchInfoForms.compositionColumns, {}, "label", "key") as MapObjectToArrayColumnEntry[];

export function formFields(dispatchList, invoiceNumberErrorMsg = "", onInvoiceNumberBlur) {
  return [
    {
      sectionTitle: dispatchInfoForms.dispatchInfo,
      fields: [
        {
          name: "dispatch_details",
          type: "autocomplete",
          labelKey: "label",
          label: dispatchInfoForms.dispatchDetails,
          options: dispatchList || [],
          required: true,
          md: 6,
        },
        {
          name: "expected_quantity",
          type: "number",
          label: dispatchInfoForms.expectedQuantity,
          disabled: true,
          md: 6,
        },
        {
          name: "dispatch_date",
          type: "date",
          label: dispatchInfoForms.dispatchDate,
          disabled: true,
          md: 6,
        },
        {
          name: "parent_dispatch",
          type: "text",
          label: dispatchInfoForms.parentDispatch,
          disabled: true,
          md: 6,
        },
        {
          name: "invoice_number",
          type: "text",
          label: dispatchInfoForms.invoiceNumber,
          required: true,
          md: 6,
          enableBlurHandler: true,
          onBlurErrorMsg: invoiceNumberErrorMsg || "",
          onCustomBlur: (value) => onInvoiceNumberBlur && onInvoiceNumberBlur(value),
        },
        {
          name: "invoice_date",
          type: "date",
          label: dispatchInfoForms.invoiceDate,
          maxDate: new Date(),
          clearable : true,
          required: true,
          md: 6,
        },
        {
          name: "invoice_quantity",
          type: "number",
          label: dispatchInfoForms.invoiceQuantity,
          numberDigits: "10,3",
          required: true,
          md: 6,
        },
        {
          name: "way_bill_number",
          type: "text",
          label: dispatchInfoForms.wayBillNumber,
          md: 6,
        },
        {
          name: "way_bill_date",
          type: "date",
          label: dispatchInfoForms.wayBillDate,
          maxDate: new Date(),
          clearable : true,
          md: 6,
        },
      ],
    },
  ];
}

// Default values for Create.tsx's react-hook-form instance.
export const createDispatchDefaultValues: CreateDispatchFormValues = {
  dispatch_details: null,
  expected_quantity: "",
  dispatch_date: null,
  parent_dispatch: "",
  invoice_number: "",
  invoice_date: null,
  invoice_quantity: "",
  way_bill_number: "",
  way_bill_date: null,
  density: "",
  moisture: "",
  soundbeans: "",
  browns: "",
  blacks: "",
  brokenbits: "",
  insectedbeans: "",
  bleached: "",
  husk: "",
  sticks: "",
  stones: "",
  mouldy_beans: "",
  beansretained: "",
};

// Maps getVendorDispatchList() rows into <autocomplete> options for Create.tsx.
export const formatToSelectionDispatch = (data: DispatchListRow[] = []): LabelValueOption[] =>
  data.map((v) => ({
    label: `${v.dispatch_id} - ${v.expected_quantity} - ${dateFormat(
      v.dispatch_date,
      "DD-MM-YYYY"
    )}`,
    value: String(v.dispatch_id),
    expected_quantity: v.expected_quantity,
    dispatch_date: v.dispatch_date,
  }));


// ── DynamicForm sectionDetails for Create.tsx ─────────────────────────────────
export function getCreateSectionDetails({
  poDetailsPayload,
  sectionDefs,
  showVehicleDocSection,
  vehicleSection,
  vehicleHeaderAction,
  isORM,
  combinedCompositionSection,
  documentSection,
}: {
  poDetailsPayload: TemplateFieldPayload[];
  sectionDefs: DynamicFormSection[];
  showVehicleDocSection: boolean;
  vehicleSection: ReactNode;
  vehicleHeaderAction: ReactNode;
  isORM: boolean;
  combinedCompositionSection: ReactNode;
  documentSection: ReactNode;
}): CreateSectionDetail[] {
  return [
    {
      type: "customComponent",
      sectionTitle: dispatchInfoForms.purchaseOrderDetails,
      component: <Template payload={poDetailsPayload} />,
    },
    ...sectionDefs,
    {
      type: "customComponent",
      sectionTitle: dispatchInfoForms.vehicleInformation,
      sectionSubtitle: dispatchInfoForms.vehicleInfoMandatory,
      headerAction: vehicleHeaderAction,
      component: showVehicleDocSection ? vehicleSection : null,
    },
    ...(!isORM
      ? [
          {
            type: "customComponent",
            sectionTitle: dispatchInfoForms.compositionInformation,
            component: combinedCompositionSection,
          } as CreateSectionDetail,
        ]
      : []),
    // Document Information — only for supplier types 1001/1002 (matches DispatchDetails guard)
    {
      type: "customComponent",
      sectionTitle: dispatchInfoForms.documentInformation,
      sectionSubtitle: `${dispatchInfoForms.pdfUploadMessage}`,
      component: showVehicleDocSection ? documentSection : null,
    },
  ];
}
